import { Card, CardContent, CardHeader, CardTitle, cn, Progress } from '@1trading/react';
import { LabeledValue } from '@/components/ui/labeled-value';
import { feeRunProgress } from '@/features/fee-runs/fee-run-helpers';
import type { FeeRun } from '@/services/api/fee-runs';

type CounterTone = 'success' | 'warning' | 'destructive';

const TONE_CLASS: Record<CounterTone, string> = { success: 'text-emerald-500', warning: 'text-amber-500', destructive: 'text-red-500' };

const Counter = ({ label, value, tone }: { label: string; value: number; tone?: CounterTone }) => (
  <LabeledValue label={label}>
    <span className={cn('text-xl font-semibold tabular-nums', tone && TONE_CLASS[tone])}>{value}</span>
  </LabeledValue>
);
Counter.displayName = 'Counter';

/** Live collection progress from the run's atomic counters. Shown once a run has been approved. */
export const FeeRunProgress = ({ run }: { run: FeeRun }) => {
  const { settled, total, percent } = feeRunProgress(run);
  const { counters } = run;
  const isLive = run.status === 'COLLECTING' || run.status === 'FINALISING';

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle>{isLive ? 'Collecting' : 'Collection'}</CardTitle>
        <span className="text-sm tabular-nums text-muted-foreground">
          {settled} of {total} account holders · {percent}%
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Progress aria-label="Collection progress" value={percent} />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-9">
          <Counter label="Queued" value={counters.usersQueued} />
          <Counter label="Done" tone="success" value={counters.usersDone} />
          <Counter label="Partial" tone="warning" value={counters.usersPartial} />
          <Counter label="Failed" tone="destructive" value={counters.usersFailed} />
          <Counter label="Charged" tone="success" value={counters.chargesCharged} />
          <Counter label="Skipped" tone="warning" value={counters.chargesSkipped} />
          <Counter label="Charge failures" tone="destructive" value={counters.chargesFailed} />
          <Counter label="Unconfirmed" tone={counters.chargesUnknown > 0 ? 'warning' : undefined} value={counters.chargesUnknown} />
          <Counter label="Ledger failures" tone={counters.fundsFailed > 0 ? 'destructive' : undefined} value={counters.fundsFailed} />
        </div>
        {counters.chargesUnknown > 0 ? (
          <p className="text-sm text-amber-500">
            {counters.chargesUnknown} {counters.chargesUnknown === 1 ? 'charge' : 'charges'} unconfirmed: the exchange did not answer. Retry once collection settles to reconcile
            them against the exchange’s adjustments log.
          </p>
        ) : null}
        {run.enqueue && run.enqueue.failed > 0 ? (
          <p className="text-sm text-amber-500">
            {run.enqueue.failed} of {run.enqueue.requested} account holders could not be queued. Use Retry once collection settles to queue them again.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
};
FeeRunProgress.displayName = 'FeeRunProgress';
